import React, { useEffect, useState, useMemo, useRef } from 'react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import dayjs from 'dayjs';
import useGetOrderDetails from '@/Hooks/RenewalApi/useGetOrderDetails';
import useSendRenewalPaymentSuccessWhatsapp from '@/Hooks/RenewalApi/useSendRenewalPaymentSuccessWhatsapp';

export type CoachType = 'general' | 'international_master' | 'grand_master';

interface SuccessPageProps {
  orderId?: string;
  amountPaid?: number;
  currency?: string;
  programName?: string;
  planType?: 'personal' | 'group' | 'webinar';
  duration?: number;
  sessions?: number;
  selectedCoach?: CoachType;
  firstClassDate?: string;
  firstClassTime?: string;
  coachName?: string;
  selectedBatch?: {
    batch_name: string;
    batch_code: string;
    coach_name: string;
    class_timing: string;
    class_days: string;
    start_date: string;
    tournament_id: number;
    level_name: string | null;
  } | null;
  onViewSchedule?: () => void;
  onDownloadInvoice?: () => void;
}

export function SuccessPage({
  orderId: propOrderId,
  amountPaid: propAmountPaid,
  currency: propCurrency = 'INR',
  programName: propProgramName,
  planType: propPlanType,
  duration: propDuration,
  sessions: propSessions,
  selectedCoach: propSelectedCoach = 'general',
  firstClassDate: propFirstClassDate,
  firstClassTime: propFirstClassTime,
  coachName: propCoachName = 'Sarah Johnson',
  selectedBatch: propSelectedBatch = null,
  onViewSchedule,
  onDownloadInvoice,
}: SuccessPageProps) {
  const [isAnimated, setIsAnimated] = useState(false);
  const whatsappSentRef = useRef(false); // Track if WhatsApp already sent

  // Read order_id and status from URL query params
  const orderIdFromUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get('order_id');
    }
    return null;
  }, []);

  const paymentStatusFromUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get('status');
    }
    return null;
  }, []);

  const orderIdToFetch = orderIdFromUrl || propOrderId;

  // Redirect to dashboard if order_id is not present in URL and not provided as prop
  useEffect(() => {
    if (!orderIdFromUrl && !propOrderId && typeof window !== 'undefined') {
      // Redirect to dashboard
      window.location.href = '/Dashboard';
      return;
    }
  }, [orderIdFromUrl, propOrderId]);

  // Fetch order details if order_id is available
  const { data: orderData, isLoading, error } = useGetOrderDetails(
    orderIdToFetch || undefined,
    { enabled: !!orderIdToFetch },
  );

  // Determine payment status from URL query params
  const paymentStatus = useMemo(() => {
    if (paymentStatusFromUrl) {
      const status = paymentStatusFromUrl.toUpperCase();
      if (status === 'CHARGED') {
        return 'success';
      } else if (status === 'PENDING_VBV' || status === 'AUTHORIZING') {
        return 'pending';
      } else if (status === 'AUTHORIZATION_FAILED' || status === 'AUTHENTICATION_FAILED') {
        return 'failed';
      }
    }
    // Default to success for backward compatibility
    return 'success';
  }, [paymentStatusFromUrl]);

  // Check if error is 404
  const is404Error = useMemo(() => {
    if (error && typeof error === 'object') {
      // Check if error has status code 404
      const errorStatus = (error as any)?.response?.status || (error as any)?.status;
      return errorStatus === 404;
    }
    return false;
  }, [error]);

  // Hook for sending payment success WhatsApp
  const { mutate: sendPaymentSuccessWhatsapp } = useSendRenewalPaymentSuccessWhatsapp(
    () => {
      // WhatsApp sent successfully
      whatsappSentRef.current = true;
    },
    (error) => {
      // Log error but don't show to user
      console.error('Error sending payment success WhatsApp:', error);
    },
  );

  useEffect(() => {
    // Trigger animation on mount
    setIsAnimated(true);

    // If we're in an iframe, notify parent window that payment is complete
    if (typeof window !== 'undefined' && window.self !== window.top) {
      const orderId = orderIdToFetch || propOrderId;
      if (orderId) {
        window.parent.postMessage({
          type: 'PAYMENT_COMPLETE',
          orderId: orderId,
          status: paymentStatusFromUrl,
        }, '*');
      }
    }
  }, [orderIdToFetch, propOrderId, paymentStatusFromUrl]);

  // Use fetched data or fallback to props
  const orderId = orderData?.order_id || orderIdToFetch || 'CSOC-2025-11-123456';
  const amountPaid = orderData?.amount_paid ?? propAmountPaid ?? 0;
  const currency = orderData?.currency || propCurrency || 'INR';
  const programName = orderData?.program_name || propProgramName || 'CSOC Program';
  const planType = orderData?.plan_type || propPlanType || 'personal';
  
  // Extract duration and sessions from renewal_meta if available, otherwise use from orderData/props
  const renewalMeta = orderData?.renewal_meta;
  let duration = orderData?.duration || propDuration || 0;
  let sessions = orderData?.sessions || propSessions || 0;
  
  if (renewalMeta && typeof renewalMeta === 'object') {
    if ('duration_months' in renewalMeta) {
      duration = renewalMeta.duration_months || duration;
    } else if ('duration' in renewalMeta) {
      duration = renewalMeta.duration || duration;
    }
    
    if ('total_sessions' in renewalMeta) {
      sessions = renewalMeta.total_sessions || sessions;
    } else if ('sessions' in renewalMeta) {
      sessions = renewalMeta.sessions || sessions;
    }
  }
  
  // Send payment success WhatsApp when orderId is available (only once)
  // Skip sending if API returns 404 error
  useEffect(() => {
    if (orderId && !whatsappSentRef.current && !isLoading && !is404Error) {
      // Extract plan_type and duration_months from orderData or props
      const planTypeToSend = orderData?.plan_type || planType;
      const durationToSend = duration;
      const amountToSend = amountPaid;

      if(!orderId?.includes('CSOC-')){sendPaymentSuccessWhatsapp({
        order_id: orderId,
        plan_type: planTypeToSend,
        duration_months: durationToSend,
        amount: amountToSend,
        currency: orderData?.currency || propCurrency || 'INR',
      });}
    }
  }, [orderId, orderData, isLoading, is404Error, planType, duration, amountPaid, sendPaymentSuccessWhatsapp]);
  
  const selectedCoach = orderData?.selected_coach || propSelectedCoach || 'general';
  const firstClassDate = orderData?.first_class_date || propFirstClassDate;
  const firstClassTime = orderData?.first_class_time || propFirstClassTime;
  const coachName = orderData?.coach_name || propCoachName;
  const hasChangeRequest = orderData?.has_change_request || false;
  const requestedDays = orderData?.requested_days;
  const requestedTime = orderData?.requested_time;

  const formatCurrency = (amount: number, curr: string = 'INR'): string => {
    if (curr === 'INR') {
      return `₹${amount.toLocaleString('en-IN')}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
    }).format(amount);
  };

  const getPlanTypeLabel = () => {
    switch (planType) {
      case 'personal':
        return 'Personal 1:1';
      case 'group':
        return 'Small Group';
      case 'webinar':
        return 'Masterclass';
      default:
        return 'Plan';
    }
  };

  // Show loading state
  if (isLoading && orderIdToFetch) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.loadingContainer}>
            <p style={styles.loadingText}>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && orderIdToFetch) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.errorContainer}>
            <p style={styles.errorText}>
              {error instanceof Error ? error.message : 'Failed to load order details'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Format first class date/time based on change request status
  let defaultFirstClassDate: string | null = null;
  let formattedFirstClassTime: string | null = null;
  let showFirstClassCard = false;

  if (hasChangeRequest && requestedDays && requestedTime) {
    // Show requested schedule for change request
    showFirstClassCard = true;
    defaultFirstClassDate = requestedDays;
    formattedFirstClassTime = requestedTime;
  } else if (firstClassDate && firstClassTime) {
    // Show normal first class date/time
    showFirstClassCard = true;
    defaultFirstClassDate = firstClassDate.includes('T')
      ? dayjs(firstClassDate).format('dddd, MMM D, YYYY')
      : firstClassDate || dayjs().add(3, 'days').format('dddd, MMM D, YYYY');
    
    if (firstClassTime.includes('T')) {
      const timeObj = dayjs(firstClassTime);
      formattedFirstClassTime = `${timeObj.format('h:mm A')} - ${timeObj.add(90, 'minutes').format('h:mm A')} IST`;
    } else if (!firstClassTime.includes('-')) {
      try {
        const timeObj = dayjs(`2000-01-01 ${firstClassTime}`);
        formattedFirstClassTime = `${timeObj.format('h:mm A')} - ${timeObj.add(90, 'minutes').format('h:mm A')} IST`;
      } catch {
        formattedFirstClassTime = firstClassTime;
      }
    } else {
      formattedFirstClassTime = firstClassTime;
    }
  }

  // Calculate start date for step 4
  const startDate = firstClassDate 
    ? dayjs(firstClassDate).format('MMM D')
    : dayjs().add(3, 'days').format('MMM D');

  // Get status-specific content
  const getStatusContent = () => {
    switch (paymentStatus) {
      case 'pending':
        return {
          icon: 'clock-outline',
          iconColor: '#FACF47',
          heading: 'Payment Processing',
          subtext: 'Your payment is being processed. Please wait while we confirm your transaction.',
        };
      case 'failed':
        return {
          icon: 'close-circle',
          iconColor: '#DC2626',
          heading: 'Payment Failed',
          subtext: 'Unfortunately, your payment could not be processed. Please try again or contact support.',
        };
      default: // success
        return {
          icon: 'check-circle',
          iconColor: '#4D3F37',
          heading: 'Payment Successful!',
          subtext: 'Your renewal is confirmed. Welcome to the next level of your learning journey!',
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Status Animation Section */}
        <div style={styles.successSection}>
          <div
            style={{
              ...styles.successBadge,
              backgroundColor: paymentStatus === 'failed' ? '#FEE2E2' : paymentStatus === 'pending' ? '#FEF3C7' : styles.successBadge.backgroundColor,
              transform: isAnimated ? 'scale(1)' : 'scale(0)',
              transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <MaterialCommunityIcons name={statusContent.icon as any} size={56} color={statusContent.iconColor} />
          </div>
          <h1 style={styles.heading}>{statusContent.heading}</h1>
          <p style={styles.subtext}>
            {statusContent.subtext}
          </p>
        </div>

        {/* Confirmation Card - Only show for success status */}
        {paymentStatus === 'success' && (
        <div style={styles.confirmationCard}>
          {/* Top Row */}
          <div style={styles.topRow}>
            <div>
              <p style={styles.label}>Order ID</p>
              <p style={styles.value}>{orderId}</p>
            </div>
            <div style={styles.amountContainer}>
              <p style={styles.label}>Amount</p>
              <p style={styles.value}>{formatCurrency(amountPaid, currency)}</p>
            </div>
          </div>

          {/* Program Details */}
          <div style={styles.programSection}>
            <p style={styles.label}>Program</p>
            <p style={styles.programValue}>
              {programName} - {getPlanTypeLabel()}
            </p>
          </div>

          {/* Two Column Grid */}
          <div style={styles.gridRow}>
            <div>
              <p style={styles.label}>Duration</p>
              <p style={styles.value}>{duration} months</p>
            </div>
            <div style={styles.sessionsContainer}>
              <p style={styles.label}>Sessions</p>
              <p style={styles.value}>{sessions} classes</p>
            </div>
          </div>

          {/* Renewal Meta Information - Show in main card */}
          {renewalMeta && Object.keys(renewalMeta).length > 0 && (
            <div style={styles.renewalMetaSection}>
              <p style={styles.label}>Renewal Details</p>
              <div style={styles.renewalMetaContent}>
                {Object.entries(renewalMeta).map(([key, value]) => {
                  // Skip duration and sessions as they're already shown above
                  if (key === 'duration_months' || key === 'duration' || key === 'total_sessions' || key === 'sessions') {
                    return null;
                  }
                  return (
                    <div key={key} style={styles.renewalMetaRow}>
                      <span style={styles.renewalMetaKey}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                      </span>
                      <span style={styles.renewalMetaValue}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Batch Details Card - Only show for group plan type with selected batch */}
        {paymentStatus === 'success' && planType === 'group' && propSelectedBatch && (
          <div style={styles.nextClassCard}>
            <div style={styles.nextClassIcon}>
              <MaterialCommunityIcons name="account-group" size={24} color="#FACF47" />
            </div>
            <div style={styles.nextClassContent}>
              <h2 style={styles.nextClassTitle}>Your Selected Batch</h2>
              {/* Compact 2-line display */}
              <div style={{marginBottom: '8px'}}>
                <p style={{...styles.nextClassDetails, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                  <span style={{fontWeight: 600, flex: '1 1 120px'}}>{propSelectedBatch.batch_name}</span>
                  {propSelectedBatch.coach_name && (
                    <span style={{fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px'}}>
                      <MaterialCommunityIcons name="account" size={12} color="#666" />
                      {propSelectedBatch.coach_name}
                    </span>
                  )}
                </p>
                <p style={{...styles.nextClassInfo, fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', margin: 0}}>
                  {propSelectedBatch.class_days && (
                    <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                      <MaterialCommunityIcons name="calendar-week" size={12} color="#666" />
                      {propSelectedBatch.class_days.split(',').map(d => {
                        const dayMap: {[key: string]: string} = {
                          '1': 'Mon', '2': 'Tue', '3': 'Wed',
                          '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun'
                        };
                        return dayMap[d.trim()] || d.trim();
                      }).join(', ')}
                    </span>
                  )}
                  {propSelectedBatch.class_timing && (
                    <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                      <MaterialCommunityIcons name="clock-outline" size={12} color="#666" />
                      {propSelectedBatch.class_timing.split(',').slice(0, 2).join(', ')}
                      {propSelectedBatch.class_timing.split(',').length > 2 ? '...' : ''}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Next Class Info Card - Only show if we have date/time info and success status (for personal/webinar) */}
        {paymentStatus === 'success' && planType !== 'group' && showFirstClassCard && defaultFirstClassDate && formattedFirstClassTime && (
          <div style={styles.nextClassCard}>
            <div style={styles.nextClassIcon}>
              <MaterialCommunityIcons name="calendar" size={24} color="#FACF47" />
            </div>
            <div style={styles.nextClassContent}>
              <h2 style={styles.nextClassTitle}>
                {hasChangeRequest ? 'Your Requested Schedule' : 'Your First Class'}
              </h2>
              <p style={styles.nextClassDetails}>
                {defaultFirstClassDate} • {formattedFirstClassTime}
              </p>
              <p style={styles.nextClassInfo}>
                {hasChangeRequest ? (
                  <>Our support team will get in touch with you to finalize your schedule.</>
                ) : (
                  <>
                    You'll receive a calendar invite and Zoom link via email within 24 hours.{' '}
                    {coachName ? (
                      <>Your coach {coachName} will reach out to introduce herself.</>
                    ) : (
                      <>Your coach will reach out to introduce herself.</>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons - Hidden as per requirements */}
        {/* <div style={styles.actionButtons}>
          <button
            style={styles.primaryButton}
            onClick={onViewSchedule}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#D4A017';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FACF47';
            }}
          >
            <MaterialCommunityIcons name="calendar" size={20} color="#4D3F37" />
            <span>View Schedule</span>
          </button>
          <button
            style={styles.secondaryButton}
            onClick={onDownloadInvoice}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#FAF8F5';
              e.currentTarget.style.borderColor = '#D4A017';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FFFFFF';
              e.currentTarget.style.borderColor = '#E8E3DC';
            }}
          >
            <MaterialCommunityIcons name="download" size={20} color="#4D3F37" />
            <span>Download Invoice</span>
          </button>
        </div> */}

        {/* Retry Button for Failed Status */}
        {paymentStatus === 'failed' && (
          <div style={styles.retrySection}>
            <button
              style={styles.retryButton}
              onClick={() => {
                // Navigate back to renewal page
                if (typeof window !== 'undefined') {
                  window.location.href = '/Renewal';
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D4A017';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FACF47';
              }}
            >
              <MaterialCommunityIcons name="refresh" size={20} color="#4D3F37" />
              <span>Try Again</span>
            </button>
          </div>
        )}

        {/* What Happens Next Section - Only show for success status */}
        {paymentStatus === 'success' && (
        <div style={styles.whatHappensNext}>
          <h2 style={styles.whatHappensTitle}>What Happens Next?</h2>
          
          <div style={styles.stepsContainer}>
            {/* Step 1 */}
            <div style={styles.step}>
              <div style={styles.stepNumber}>1</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>Check Your Email</h3>
                <p style={styles.stepDescription}>
                  You'll receive a confirmation email with your invoice and course details within 5 minutes.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div style={styles.step}>
              <div style={styles.stepNumber}>2</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>Support Will Handle Your Order</h3>
                <p style={styles.stepDescription}>
                  Our support team will handle this order and will revert to you shortly.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div style={styles.step}>
              <div style={styles.stepNumber}>3</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>Start Learning!</h3>
                <p style={styles.stepDescription}>
                  Begin your journey on {startDate} and unlock your chess potential.
                </p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Pending Status Message */}
        {paymentStatus === 'pending' && (
          <div style={styles.pendingSection}>
            <p style={styles.pendingText}>
              Please do not close this page. We will update you once the payment is confirmed.
            </p>
          </div>
        )}

        {/* Footer Support Links */}
        {/* <div style={styles.footer}>
          <p style={styles.footerText}>Questions? We're here to help!</p>
          <div style={styles.footerLinks}>
            <a
              href="#"
              style={styles.footerLink}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#D4A017';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#8A7968';
              }}
            >
              Contact Support
            </a>
            <span style={styles.footerSeparator}>•</span>
            <a
              href="#"
              style={styles.footerLink}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#D4A017';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#8A7968';
              }}
            >
              View FAQs
            </a>
            <span style={styles.footerSeparator}>•</span>
            <a
              href="#"
              style={styles.footerLink}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#D4A017';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#8A7968';
              }}
            >
              WhatsApp Us
            </a>
          </div>
        </div> */}
      </div>
    </div>
  );
}

// Design System Colors
const colors = {
  background: '#FAF8F5',
  primaryCTA: '#FACF47',
  primaryCTAHover: '#D4A017',
  textPrimary: '#4D3F37',
  textSecondary: '#8A7968',
  border: '#E8E3DC',
  white: '#FFFFFF',
  darkGradientStart: '#4D3F37',
  darkGradientEnd: '#6B5C51',
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.background,
    padding: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontFamily: 'Inter, Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  content: {
    width: '100%',
    maxWidth: '672px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  successSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginTop: '40px',
    marginBottom: '8px',
  },
  successBadge: {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    backgroundColor: colors.primaryCTA,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '24px',
  },
  heading: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: '0 0 16px 0',
    lineHeight: '1.2',
  },
  subtext: {
    fontSize: '18px',
    color: colors.textSecondary,
    margin: 0,
    lineHeight: '1.5',
    maxWidth: '600px',
  },
  confirmationCard: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `2px solid ${colors.primaryCTA}`,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: '20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  amountContainer: {
    textAlign: 'right',
  },
  label: {
    fontSize: '13px',
    color: colors.textSecondary,
    margin: '0 0 6px 0',
    fontWeight: 400,
  },
  value: {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: 0,
  },
  programSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  programValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: 0,
  },
  gridRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  sessionsContainer: {
    textAlign: 'right',
  },
  nextClassCard: {
    background: `linear-gradient(135deg, ${colors.darkGradientStart} 0%, ${colors.darkGradientEnd} 100%)`,
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'flex-start',
  },
  nextClassIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: colors.primaryCTA,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  nextClassContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  nextClassTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.white,
    margin: 0,
  },
  nextClassDetails: {
    fontSize: '16px',
    color: colors.white,
    margin: 0,
    lineHeight: '1.5',
  },
  nextClassInfo: {
    fontSize: '14px',
    color: colors.white,
    opacity: 0.9,
    margin: 0,
    lineHeight: '1.5',
  },
  actionButtons: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  primaryButton: {
    backgroundColor: colors.primaryCTA,
    color: colors.textPrimary,
    border: 'none',
    borderRadius: '12px',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'background-color 0.2s ease',
  },
  secondaryButton: {
    backgroundColor: colors.white,
    color: colors.textPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
  },
  whatHappensNext: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  whatHappensTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: 0,
    textAlign: 'center',
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  step: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: colors.primaryCTA,
    color: colors.textPrimary,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: 700,
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  stepTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: 0,
  },
  stepDescription: {
    fontSize: '14px',
    color: colors.textSecondary,
    margin: 0,
    lineHeight: '1.5',
  },
  footer: {
    textAlign: 'center',
    padding: '32px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  footerText: {
    fontSize: '16px',
    color: colors.textSecondary,
    margin: 0,
  },
  footerLinks: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  footerLink: {
    fontSize: '14px',
    color: colors.textSecondary,
    textDecoration: 'underline',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  },
  footerSeparator: {
    fontSize: '14px',
    color: colors.textSecondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
    minHeight: '200px',
  },
  loadingText: {
    fontSize: '16px',
    color: colors.textSecondary,
    margin: 0,
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
    minHeight: '200px',
  },
  errorText: {
    fontSize: '16px',
    color: '#DC2626',
    margin: 0,
  },
  renewalMetaSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingTop: '20px',
    borderTop: `1px solid ${colors.border}`,
  },
  renewalMetaContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  renewalMetaRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'flex-start',
  },
  renewalMetaKey: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.textSecondary,
    minWidth: '120px',
  },
  renewalMetaValue: {
    fontSize: '13px',
    color: colors.textPrimary,
    flex: 1,
    wordBreak: 'break-word',
  },
  retrySection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '32px 0',
  },
  retryButton: {
    backgroundColor: colors.primaryCTA,
    color: colors.textPrimary,
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'background-color 0.2s ease',
  },
  pendingSection: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '32px 24px',
    textAlign: 'center',
    marginTop: '24px',
  },
  pendingText: {
    fontSize: '16px',
    color: colors.textSecondary,
    margin: 0,
    lineHeight: '1.5',
  },
};

export default SuccessPage;





